"use client";

import { createContext, useContext, useMemo } from "react";
import { useAgentOverlay } from "./useAgentOverlay";
import type { DebugRefs, AgentPromptPayload, DebriefRequestParams } from "./useAgentOverlay";
import type {
  AgentEvent,
  ConversationDebrief,
  ConversationDifficulty,
  ConversationPersona,
  LessonContextInput,
  OverlayState,
} from "./types";
import type { AgentLessonContent } from "@lib/agent/lessonSnapshot";

type AgentOverlayContextValue = {
  state: OverlayState;
  dispatchEvent: (event: AgentEvent) => void;
  sendUserMessage: (text: string) => void;
  startConversation: () => void;
  selectScenario: (
    persona: ConversationPersona,
    scenarioTitle: string,
    sessionId: number,
    difficulty: ConversationDifficulty,
  ) => void;
  sendConversationTurn: (text: string) => void;
  endConversation: (endedEarly: boolean) => void;
  requestDebrief: (params: DebriefRequestParams) => Promise<ConversationDebrief | null>;
  returnToTutor: () => void;
  retryDebrief: () => void;
  setVoiceFallback: () => void;
  setLessonContext: (ctx: LessonContextInput | null) => void;
  setLessonContent: (content: AgentLessonContent | null) => void;
  dismiss: () => void;
  open: () => void;
  debugRefs: DebugRefs;
  getPromptPayload: () => AgentPromptPayload;
};

const AgentOverlayContext = createContext<AgentOverlayContextValue | null>(null);

export function AgentOverlayProvider({ children }: { children: React.ReactNode }) {
  const {
    state,
    dispatchEvent,
    sendUserMessage,
    startConversation,
    selectScenario,
    sendConversationTurn,
    endConversation,
    requestDebrief,
    returnToTutor,
    retryDebrief,
    setVoiceFallback,
    setLessonContext,
    setLessonContent,
    dismiss,
    open,
    debugRefs,
    getPromptPayload,
  } = useAgentOverlay();
  const value = useMemo(
    () => ({
      state,
      dispatchEvent,
      sendUserMessage,
      startConversation,
      selectScenario,
      sendConversationTurn,
      endConversation,
      requestDebrief,
      returnToTutor,
      retryDebrief,
      setVoiceFallback,
      setLessonContext,
      setLessonContent,
      dismiss,
      open,
      debugRefs,
      getPromptPayload,
    }),
    // debugRefs / setLessonContext are stable - no need in deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      state,
      dispatchEvent,
      sendUserMessage,
      startConversation,
      selectScenario,
      sendConversationTurn,
      endConversation,
      requestDebrief,
      returnToTutor,
      dismiss,
      open,
    ],
  );
  return <AgentOverlayContext.Provider value={value}>{children}</AgentOverlayContext.Provider>;
}

export function useAgentOverlayContext(): AgentOverlayContextValue {
  const ctx = useContext(AgentOverlayContext);
  if (!ctx) throw new Error("useAgentOverlayContext must be used inside AgentOverlayProvider");
  return ctx;
}
